/**
 * A fixed-row-height virtualized list that stays pinned to its newest row
 * unless the user scrolls up, like Unreal's Output Log. Used by the Console.
 */
import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { visibleWindow } from "./virtualRows";

export const ROW_HEIGHT = 19;

/** How close to the bottom (px) still counts as "following" new rows. */
const STICK_THRESHOLD = 24;

export interface VirtualListProps<T> {
  readonly items: readonly T[];
  readonly rowKey: (item: T) => string | number;
  readonly renderRow: (item: T) => ReactNode;
  readonly label: string;
}

export function VirtualList<T>({ items, rowKey, renderRow, label }: VirtualListProps<T>) {
  const scroller = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const [view, setView] = useState({ scrollTop: 0, height: 0 });

  // Track the scroller's height (panels resize with the dock). A tab the dock
  // hides has no height, so pinning to the bottom did nothing then; re-pin
  // whenever the list is sized, including when its tab is shown.
  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (following.current) element.scrollTop = element.scrollHeight;
      setView({ scrollTop: element.scrollTop, height: element.clientHeight });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  // New rows keep the list at the bottom while it's following.
  useLayoutEffect(() => {
    const element = scroller.current;
    if (element && following.current) element.scrollTop = element.scrollHeight;
  }, [items.length]);

  const { start, end } = visibleWindow({
    scrollTop: view.scrollTop,
    viewportHeight: view.height,
    rowHeight: ROW_HEIGHT,
    count: items.length,
  });

  return (
    <div
      ref={scroller}
      className="kn-vlist"
      onScroll={(event) => {
        const element = event.currentTarget;
        following.current =
          element.scrollHeight - element.scrollTop - element.clientHeight < STICK_THRESHOLD;
        setView({ scrollTop: element.scrollTop, height: element.clientHeight });
      }}
    >
      <ol
        className="kn-vlist__rows"
        aria-label={label}
        style={{ height: items.length * ROW_HEIGHT }}
      >
        {items.slice(start, end).map((item, offset) => (
          <li
            key={rowKey(item)}
            className="kn-vlist__row"
            style={{ top: (start + offset) * ROW_HEIGHT }}
          >
            {renderRow(item)}
          </li>
        ))}
      </ol>
    </div>
  );
}
